import React from "react";
import { Quote } from "lucide-react";

const PASTOR_IMG = "https://customer-assets.emergentagent.com/job_ebenezer-ghana-sda/artifacts/fkebmt9v_1779417898380.png";

/**
 * PastorMessage — warm, Christ-centered welcome card from the pastor.
 * Variants:
 *   "light" (default) — for white/light backgrounds
 *   "muted"           — for #F7F8FA backgrounds
 */
export const PastorMessage = ({ variant = "light" }) => {
  const sectionBg = variant === "muted" ? "bg-[#F7F8FA]" : "bg-white";

  return (
    <section data-testid="pastor-message-section" className={`py-24 lg:py-32 ${sectionBg}`}>
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="max-w-2xl mb-12 lg:mb-16">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#D4A437] mb-5">
            Welcome
          </div>
          <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight text-[#003B5C] leading-tight">
            Message from the Pastor.
          </h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          {/* Photo + identity card */}
          <aside className="lg:col-span-4">
            <div className="bg-[#003B5C] text-white rounded-2xl overflow-hidden shadow-lg">
              <div className="aspect-[4/5] overflow-hidden bg-[#F7F8FA]">
                <img
                  src={PASTOR_IMG}
                  alt="Pastor Abraham Binzuwah Jr."
                  data-testid="pastor-photo"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="p-6 sm:p-7">
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#D4A437] font-bold mb-2">
                  Pastor
                </div>
                <div className="font-display font-bold text-lg sm:text-xl text-white leading-tight">
                  Pastor Abraham Binzuwah Jr.
                </div>
                <div className="text-sm text-white/70 mt-2 leading-snug">
                  Garland Ebenezer Ghanaian Seventh-day Adventist Church
                </div>
                <div className="mt-5 pt-5 border-t border-white/10">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-[#D4A437] font-bold mb-2">
                    About the Pastor
                  </div>
                  <p className="text-sm text-white/75 leading-relaxed">
                    Pastor Abraham Binzuwah Jr. serves as senior pastor at DeSoto Seventh-day
                    Adventist Church and maintains ministry ties with Garland Ebenezer Ghanaian
                    Seventh-day Adventist Church — walking alongside our congregation in worship,
                    discipleship, and community outreach.
                  </p>
                </div>
              </div>
            </div>
          </aside>

          {/* Letter */}
          <article className="lg:col-span-8 bg-white border border-gray-100 rounded-2xl p-8 sm:p-10 lg:p-12 shadow-sm">
            <p className="font-display text-xl sm:text-2xl font-semibold text-[#003B5C] mb-6">
              Dear friends,
            </p>
            <div className="space-y-5 text-base sm:text-lg leading-[1.8] text-[#1E1E1E]/80">
              <p>
                Welcome to <span className="font-semibold text-[#003B5C]">Garland Ebenezer Ghanaian Seventh-day Adventist Church</span>. We are grateful that you are
                visiting our church website. Our desire is that every person who comes through our
                doors will experience the love of Christ, the power of God's Word, and the warmth
                of a caring church family.
              </p>
              <p>
                As a Seventh-day Adventist congregation, we believe Jesus is the center of our
                worship, our mission, and our hope. We are committed to growing in faith, serving
                our community, strengthening families, and preparing hearts for the soon return of
                Christ.
              </p>
              <p>
                Whether you are new to the area, searching for a church home, or simply looking
                for a place to worship, we invite you to join us. You are welcome here.
              </p>
            </div>

            <div className="mt-8 pt-6 border-t border-gray-100">
              <p className="text-sm text-[#1E1E1E]/60 italic mb-1">With Christian love,</p>
              <p className="font-display text-lg font-bold text-[#003B5C]">Pastor Abraham Binzuwah Jr.</p>
              <p className="text-sm text-[#1E1E1E]/60 mt-1">Pastor, Garland Ebenezer Ghanaian Seventh-day Adventist Church and DeSoto Seventh-day Adventist Church</p>
            </div>

            {/* Scripture pull-quote */}
            <figure
              data-testid="pastor-message-verse"
              className="mt-10 relative bg-[#F7F8FA] border-l-4 border-[#D4A437] p-6 sm:p-7 rounded-r-xl"
            >
              <Quote size={22} className="text-[#D4A437] mb-3" />
              <blockquote className="font-display text-lg sm:text-xl text-[#003B5C] leading-snug italic">
                "Therefore receive one another, just as Christ also received us, to the glory of God."
              </blockquote>
              <figcaption className="mt-3 text-xs sm:text-sm font-bold uppercase tracking-[0.18em] text-[#D4A437]">
                Romans 15:7 NKJV
              </figcaption>
            </figure>
          </article>
        </div>
      </div>
    </section>
  );
};
